import { TranslocoService } from '@jsverse/transloco'
import { JFQuestionType, JFWidgetType } from '../../models/jotform.model'

/**
 * Per-question copy for the Shopify “Request SMS access” Jotform, keyed by **visible** question order
 * (non-hidden fields, same order as shown in the modal). If Jotform reorders fields, either update this
 * array order or set `SHOPIFY_SMS_QID_LAYOUT_INDEX` so each `qid` maps to the correct layout index.
 */
type ShopifySmsVisibleLayout = {
  textKey?: string
  hintKey?: string
  descriptionKey?: string
  firstNamePlaceholderKey?: string
  lastNamePlaceholderKey?: string
  termsLabelKey?: string
}

export const SHOPIFY_SMS_QID_LAYOUT_INDEX: Record<string, number> = {}

const VISIBLE_LAYOUT: ShopifySmsVisibleLayout[] = [
  {
    textKey: 'access_form_modal.shopify_sms.q_name',
    firstNamePlaceholderKey: 'access_form_modal.placeholder_first_name',
    lastNamePlaceholderKey: 'access_form_modal.placeholder_last_name',
  },
  {
    textKey: 'access_form_modal.shopify_sms.q_email',
    hintKey: 'access_form_modal.shopify_sms.hint_email',
  },
  {
    textKey: 'access_form_modal.shopify_sms.q_website',
    hintKey: 'access_form_modal.shopify_sms.hint_website',
  },
  {
    textKey: 'access_form_modal.shopify_sms.q_other_apps',
  },
  {
    textKey: 'access_form_modal.shopify_sms.q_how_collected',
  },
  {
    textKey: 'access_form_modal.shopify_sms.q_upload',
    descriptionKey: 'access_form_modal.shopify_sms.upload_help',
  },
  {
    textKey: 'access_form_modal.shopify_sms.q_phone_count',
    hintKey: 'access_form_modal.shopify_sms.hint_number',
  },
  {
    termsLabelKey: 'access_form_modal.shopify_sms.q_terms',
  },
]

const CHECKBOX_OPTION_EN_TO_KEY: Record<string, string> = {
  'Popup': 'access_form_modal.shopify_sms.opt_popup',
  'Checkout page': 'access_form_modal.shopify_sms.opt_checkout',
  'Website form': 'access_form_modal.shopify_sms.opt_website_form',
  'Text a keyword': 'access_form_modal.shopify_sms.opt_text_keyword',
}

export function applyShopifySmsAccessFormLocalization(activeForm: any, transloco: TranslocoService): void {
  if (!activeForm?.questions?.length) {
    return
  }

  const t = (key: string) => transloco.translate(key)

  if (activeForm.header) {
    activeForm.header.title = t('access_form_modal.shopify_sms.header_title')
    activeForm.header.subtitle = t('access_form_modal.shopify_sms.header_subtitle')
  }
  activeForm.description = t('access_form_modal.shopify_sms.intro_html')

  const visible = activeForm.questions.filter((q: any) => !q.hidden)

  visible.forEach((question: any, i: number) => {
    const qid = String(question.qid)
    const layoutIndex = SHOPIFY_SMS_QID_LAYOUT_INDEX[qid] ?? i
    const layout = VISIBLE_LAYOUT[layoutIndex]
    if (!layout) {
      return
    }

    switch (question.type) {
      case JFQuestionType.ControlFullname:
        if (layout.textKey) {
          question.text = t(layout.textKey)
        }
        if (question.placeholder) {
          question.placeholder.first = t(layout.firstNamePlaceholderKey || 'access_form_modal.placeholder_first_name')
          question.placeholder.last = t(layout.lastNamePlaceholderKey || 'access_form_modal.placeholder_last_name')
        }
        break
      case JFQuestionType.ControlEmail:
        if (layout.textKey) {
          question.text = t(layout.textKey)
        }
        if (layout.hintKey) {
          question.hint = t(layout.hintKey)
        }
        break
      case JFQuestionType.ControlTextbox:
        if (layout.textKey) {
          question.text = t(layout.textKey)
        }
        if (layout.hintKey) {
          question.hint = t(layout.hintKey)
        }
        break
      case JFQuestionType.ControlTextarea:
        if (layout.textKey) {
          question.text = t(layout.textKey)
        }
        break
      case JFQuestionType.ControlCheckbox:
        if (layout.textKey) {
          question.text = t(layout.textKey)
        }
        if (question.options?.length) {
          question.options.forEach((opt: any) => {
            if (opt.type === 'other') {
              opt.placeholder = t('access_form_modal.placeholder_other')
              return
            }
            const raw = String(opt.value || '').trim()
            const optKey = CHECKBOX_OPTION_EN_TO_KEY[raw]
            if (optKey) {
              opt.displayLabel = t(optKey)
            }
          })
        }
        break
      case JFQuestionType.ControlFileUpload:
        if (layout.textKey) {
          question.text = t(layout.textKey)
        }
        if (layout.descriptionKey) {
          question.description = t(layout.descriptionKey)
        }
        break
      case JFQuestionType.ControlNumber:
        if (layout.textKey) {
          question.text = t(layout.textKey)
        }
        if (layout.hintKey) {
          question.hint = t(layout.hintKey)
        }
        break
      case JFQuestionType.ControlWidget:
        if (question.widgetType === JFWidgetType.Field && layout.termsLabelKey) {
          question.label = t(layout.termsLabelKey)
        }
        break
      default:
        break
    }
  })
}

